import React, { useState } from 'react';
import { Button, Spin } from 'antd';
import cx from 'classnames';
import type { ManagedBillingVo } from 'types/insuranceBilling';
import EhrBadge from '../EhrBadge';
import { ehrByName } from '../../constants';
import TurnOffModal from './TurnOffModal';
import TurnOnModal from './TurnOnModal';
import RestoreEhrModal from './RestoreEhrModal';
import { formatDay, stoppedEhrs } from './utils';
import s from './s.module.less';

type Props = {
    state: ManagedBillingVo | null;
    loading: boolean;
    isGroup: boolean;
    selfProviderId?: number;
    onChange: (next: ManagedBillingVo) => void;
};

// 原型 claimSubmissionReadHtml：Settings › Payment 底部的 Managed billing 区块。
// On：Active since + 改过的 EHR 数；每个停掉的 EHR 单独一行带 Turn on（否则部分关闭是单向门）。
// Off：Turned off {date} · {reason} + Turn on
export default function ManagedBillingSection({ state, loading, isGroup, selfProviderId, onChange }: Props) {
    const [offOpen, setOffOpen] = useState(false);
    const [onOpen, setOnOpen] = useState(false);
    const [restoreEhr, setRestoreEhr] = useState<string | null>(null);

    // 拉取失败（非 owner / AskChart 不可用）不展示区块
    if (!state) {
        return loading ? (
            <div className={s.section}>
                <Spin size="small" />
            </div>
        ) : null;
    }

    const done = (next: ManagedBillingVo) => {
        setOffOpen(false);
        setOnOpen(false);
        setRestoreEhr(null);
        if (next) onChange(next);
    };

    const stopped = state.enabled ? stoppedEhrs(state) : [];
    let sub: string;
    if (!state.enabled) {
        sub = [`Turned off ${formatDay(state.pausedAt)}`, state.pausedReason].filter(Boolean).join(' · ');
    } else {
        const parts = [state.activatedAt ? `Active since ${formatDay(state.activatedAt)}` : 'Active'];
        if (stopped.length) {
            parts.push(
                `${stopped.length} ${stopped.length === 1 ? 'EHR' : 'EHRs'} changed ${formatDay(state.pausedAt)}`.trim(),
            );
        }
        sub = parts.join(' · ');
    }

    const modalProps = { state, isGroup, selfProviderId, onDone: done };

    return (
        <div className={s.section}>
            <div className={s.sectionHead}>Managed billing</div>
            <div className={cx(s.row, !state.enabled && s.rowOff)}>
                <div>
                    <div className={s.rowName}>Claim submission</div>
                    <div className={s.rowSub}>{sub}</div>
                </div>
                <div className={s.rowRight}>
                    <span className={cx(s.status, state.enabled && s.statusOn)}>
                        <span className={s.dot} />
                        {state.enabled ? 'On' : 'Off'}
                    </span>
                    {state.enabled ? (
                        <Button onClick={() => setOffOpen(true)}>Turn off</Button>
                    ) : (
                        <Button type="primary" onClick={() => setOnOpen(true)}>
                            Turn on
                        </Button>
                    )}
                </div>
            </div>
            {stopped.length > 0 && (
                <div className={s.stoppedList}>
                    {stopped.map((r) => (
                        <div key={r.ehr} className={s.stoppedRow}>
                            <EhrBadge ehr={ehrByName(r.ehr)} />
                            <span className={s.stoppedName}>{r.ehr}</span>
                            <span className={s.stoppedTag}>{r.label}</span>
                            <Button type="link" className={s.linkBtn} onClick={() => setRestoreEhr(r.ehr)}>
                                Turn on
                            </Button>
                        </div>
                    ))}
                </div>
            )}
            <TurnOffModal open={offOpen} onClose={() => setOffOpen(false)} {...modalProps} />
            <TurnOnModal open={onOpen} onClose={() => setOnOpen(false)} {...modalProps} />
            <RestoreEhrModal ehr={restoreEhr} onClose={() => setRestoreEhr(null)} {...modalProps} />
        </div>
    );
}
